import React, { useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { MarkdownDisplay, TextView } from '@ygtechnologies/bygr-frontend-component-library';
import { Button, ButtonBar, Dialog } from '@ygtechnologies/rubus-ui';

import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';
import { ThumbnailGallery, ThumbnailGalleryImage } from '@/components/ThumbnailGallery/ThumbnailGallery';
import { ConfiguratorPresetDto } from '@/services/configurator';
import { useConfiguratorMutations } from '@/components/OptionConfigurator/ConfiguratorMutationContext';
import { useImageUrls } from '@/components/OptionConfigurator/useImageUrls';

import { IncludedOptionsList } from './IncludedOptionsList';
import theme from './PresetDialog.module.scss';

interface Props {
    show: boolean;
    style: ConfiguratorPresetDto | null;
    deadlineId: number;
    roomId: number | null;
    canChange: boolean;
    onClose: () => void;
    onApplied?: () => void;
}

interface BodyProps {
    style: ConfiguratorPresetDto;
    deadlineId: number;
    roomId: number | null;
    onLightboxToggle: (open: boolean) => void;
}

const ApplyDialogBody: React.FC<BodyProps> = ({ style, deadlineId, roomId, onLightboxToggle }) => {
    const { getOptionPresetImageUrl, getOptionPresetImageThumbnailUrl } = useImageUrls();
    const images = useMemo<ThumbnailGalleryImage[]>(
        () =>
            style.images.map((img) => ({
                url: getOptionPresetImageUrl(img.id),
                thumbnailUrl: getOptionPresetImageThumbnailUrl(img.id),
            })),
        [style, getOptionPresetImageUrl, getOptionPresetImageThumbnailUrl]
    );

    return (
        <div className={theme.dialogBody}>
            <ThumbnailGallery
                useFullSizeImage
                images={images}
                className={theme.headerImageContainer}
                imageClassName={theme.headerImage}
                onLightboxToggle={onLightboxToggle}
            />

            <div className={theme.topSection}>
                <div className={theme.info}>
                    <h1 className={theme.styleHeader}>{style.name}</h1>
                    <TextView className="white-space-collapse" numberOfLines={8}>
                        <MarkdownDisplay
                            className="text-view-markdown-fixer"
                            markdown={style.description}
                        />
                    </TextView>
                </div>
                <div className={theme.priceSection}>
                    <div className={theme.priceRow}>
                        <Trans>Package price:</Trans>
                        <span className={theme.priceAmount}>
                            <BuyerCurrencyDisplay value={style.price} />
                        </span>
                    </div>
                </div>
            </div>

            <IncludedOptionsList style={style} deadlineId={deadlineId} roomId={roomId} />
        </div>
    );
};

export const PresetApplyDialog: React.FC<Props> = ({
    show,
    style,
    deadlineId,
    roomId,
    canChange,
    onClose,
    onApplied,
}) => {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const { pickStyle, isPickingStyle } = useConfiguratorMutations();

    if (!show || !style) return null;

    const handleApply = () => {
        if (!deadlineId) {
            onClose();
            return;
        }
        // Fire-and-forget through the shared mutation provider (API or Showroom), passing this
        // preset group's own deadline since the legacy page spans every deadline. The cache update
        // is handled by the provider, so we can close immediately (mirrors the v2 flow).
        pickStyle(style.id, roomId ?? undefined, deadlineId);
        onApplied?.();
        onClose();
    };

    return (
        <Dialog
            open={show}
            onOpenChange={(open) => !open && onClose()}
            closeOnClickOutside={!lightboxOpen}
            closeOnEscape={!lightboxOpen}>
            <Dialog.Content contentWidth={canChange ? 'lg' : 'md'}>
                <Dialog.Header>
                    <Trans>Apply a style to your home</Trans>
                </Dialog.Header>
                {!canChange ? (
                    <Trans>Cannot change style after options have been sent</Trans>
                ) : (
                    <ApplyDialogBody
                        style={style}
                        deadlineId={deadlineId}
                        roomId={roomId}
                        onLightboxToggle={setLightboxOpen}
                    />
                )}
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={onClose} disabled={isPickingStyle}>
                            <Trans>Close</Trans>
                        </Button>
                        {canChange && (
                            <Button
                                variant="primary"
                                onClick={handleApply}
                                loading={isPickingStyle}
                                disabled={!deadlineId || isPickingStyle}>
                                <Trans>Select style</Trans>
                            </Button>
                        )}
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};
